import { ILoginResponse, LoginService } from "@app/services/login.service.ajs";
import { IPhoneNumberOutput } from "@directives/_forms/smsInput/smsInput.interfaces";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { SpecialUserPermissions } from "@enums/SpecialUserPermissions";
import { UserStatus } from "@enums/UserType";
import { IDriver } from "@interfaces/IDriver";
import { StringPreference } from "@interfaces/IGenericPreference";
import { IGeofence } from "@interfaces/IGeofences";
import { IHttpException } from "@interfaces/IHttpException";
import { IPreferencesContent } from "@interfaces/IPreferencesContent";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IAccountUserMapping, IUser, UserPlatform } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { AccountService, IAccount } from "@services/account.service";
import { AccountFeatureService, FeatureAccessType, IAccountFeatureDescription, IAccountFeatureDescriptionMap, ISpecialUserPermissionDescriptionMap } from "@services/accountFeature.service";
import { AccountUserMappingService } from "@services/accountUserMapping.service.ajs";
import { DriverService } from "@services/driver.service";
import { GeofenceService } from "@services/geofence.service";
import { GroupService } from "@services/group.service.ajs";
import { ILoginHistory, LoginHistoryService } from "@services/loginHistory.service.ajs";
import { MessageDialogService } from "@services/messageDialog.service";
import { PreferencesService } from "@services/preferences.service";
import { ITranslationLocale, TranslationService } from "@services/translation.service";
import { UserMappingsService } from "@services/userMappings.service";
import { UsersService } from "@services/users.service";
import { ValidationService } from "@services/validation.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import { copy } from "angular";
import { FoundationAccountService } from "../../../../modules/foundation/_services/account.service.ajs";
import { IGroup } from "../../groups/_models/IGroup";
import { IGroupWithIds } from "../../groups/_models/IGroupWithIds";
import { IResourceGroupListEntry } from "../../groups/_models/IResourceGroupListEntry";
import './userEditor.component.scss';


interface IComponentBindings {
    user: IUser;
    customerId: number;
    onSaved: (args: { user: IUser }) => void;
    onCreated: (args: { user: IUser }) => void;
    onDeactivated: (args: { user: IUser }) => void;
    onReactivated: (args: { user: IUser }) => void;
    onCancelled: () => void;
}

class UserEditorComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    user: IUser;
    customerId: number;
    onSaved: (args: { user: IUser }) => void;
    onCreated: (args: { user: IUser }) => void;
    onDeactivated: (args: { user: IUser }) => void;
    onReactivated: (args: { user: IUser }) => void;
    onCancelled: () => void;

    // raw data
    account: IAccount;
    vehicles: IVehicle[];
    drivers: IDriver[];
    geofences: IGeofence[];
    groups: IGroup[];
    accountFeatureDescriptions: IAccountFeatureDescription[];
    accountFeatureDescriptionsMap: IAccountFeatureDescriptionMap;
    specialUserPermissionsDescriptionsMap: ISpecialUserPermissionDescriptionMap;
    currentUserOnFoundationPlatform: boolean;
    locales: ITranslationLocale[];

    // form changes
    userForm: any;
    dirtyUser: IUser;
    hasUnsavedChanges: boolean;

    // spinners
    loading: boolean;
    saving: boolean;
    deactivating: boolean;
    reactivating: boolean;
    resettingPassword: boolean;

    // state
    cachedSelectedVehicleIds: number[];
    cachedSelectedDriverIds: number[];
    cachedSelectedGeofenceIds: number[];
    totalAssignedGroups: number;
    totalAssignedVehicles: number;
    totalAssignedDrivers: number;
    totalAssignedGeofences: number;
    lastLoggedInDate: string;
    isPhoneValid: boolean;
    isFormValid: boolean;

    get isActiveUser(): boolean {
        return this.dirtyUser && this.dirtyUser.Id > 0 && this.dirtyUser.CurrentAccountUserMapping.Status === UserStatus.Active;
    }

    get isInactiveUser(): boolean {
        return this.dirtyUser && this.dirtyUser.Id > 0 && this.dirtyUser.CurrentAccountUserMapping.Status === UserStatus.Inactive;
    }

    // enums
    SpecialUserPermission = SpecialUserPermissions;
    AccountFeature = AccountFeatures;

    static $inject = ["$q", "$scope", "$rootScope", "$timeout", "$mdDialog", "veriDate", "loginService", "groupService", "usersService", "driverService", "vehicleService", "accountService", "geofenceService", "validationService", "accountFeatureService", "translationService", "loginHistoryService", "messageDialogService", "accountUserMappingService", "foundationAccountService", "userMappingsService", "preferencesService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly veriDate: VeriDate,
        private readonly loginService: LoginService,
        private readonly groupService: GroupService,
        private readonly usersService: UsersService,
        private readonly driverService: DriverService,
        private readonly vehicleService: VehicleService,
        private readonly accountService: AccountService,
        private readonly geofenceService: GeofenceService,
        private readonly validationService: ValidationService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly translationService: TranslationService,
        private readonly loginHistoryService: LoginHistoryService,
        private readonly messageDialogService: MessageDialogService,
        private readonly accountUserMappingService: AccountUserMappingService,
        private readonly foundationAccountService: FoundationAccountService,
        private readonly userMappingsService: UserMappingsService,
        private readonly preferencesService: PreferencesService
    ) { }

    $onInit() {
        this.currentUserOnFoundationPlatform = this.usersService.isCurrentUserOnFoundationPlatform();
        this.prepareUserModel();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (onChangesObj["user"]) {
            this.prepareUserModel();
        }
    }

    save(): angular.IPromise<void> {
        if (this.saving) {
            return;
        }

        this.saving = true;

        return this.createUserSavePromise().then((accountUserMapping: IAccountUserMapping) => {

            const additionalSavePromises: angular.IPromise<any>[] = [];

            if (this.dirtyUser.Id === -1) {
                additionalSavePromises.push(this.createUserPreferencesSavePromise(accountUserMapping));
            }

            additionalSavePromises.push(this.createResourceMappingsSavePromise());

            return this.$q.all(additionalSavePromises).then(() => {

                this.usersService.updateOrInsertUserCache(this.dirtyUser);

                if (this.dirtyUser.Id > 0) {
                    if (!this.onSaved) {
                        return;
                    }

                    this.onSaved({ user: this.dirtyUser });
                }
                else {
                    if (!this.onCreated) {
                        return;
                    }

                    this.onCreated({ user: this.dirtyUser });
                }

            }).catch((err: angular.IHttpResponse<IHttpException>) => {
                this.messageDialogService.handleError(err, this.translationService.t("UserEditor.UnableToSaveUser"))
            }).finally(() => {
                this.saving = false;
            });
        });
    }

    deactivate(): void {
        if (this.deactivating) {
            return;
        }


        this.deactivating = true;

        this.usersService.deactivateUser(this.user).then(() => {
            if (!this.onDeactivated) {
                return;
            }

            this.onDeactivated({ user: this.user });

        }).catch((err: angular.IHttpResponse<IHttpException>) => {
            this.messageDialogService.handleError(err, this.translationService.t("UserEditor.UnableToDeleteUser"))

        }).finally(() => {
            this.deactivating = false;
        });
    }

    reactivate(): void {
        if (this.reactivating) {
            return;
        }

        this.reactivating = true;

        this.usersService.reactivateUser(this.user).then(() => {
            if (!this.onReactivated) {
                return;
            }

            this.onReactivated({ user: this.user });

        }).catch((err: angular.IHttpResponse<IHttpException>) => {
            this.messageDialogService.handleError(err, this.translationService.t("UserEditor.UnableToActivateUser"))

        }).finally(() => {
            this.reactivating = false;
        });
    }

    cancel(): void {
        if (!this.onCancelled) {
            return;
        }

        this.onCancelled();
    }

    resetPassword($event: MouseEvent): void {
        let content = this.translationService.t("UserEditor.ResetPasswordWillSendEmail", [this.dirtyUser.CurrentAccountUserMapping.Name, this.dirtyUser.Email]);

        if (!this.validateEmail(this.dirtyUser.Email)) {
            content += `<br /><br />`;
            content += this.translationService.t("UserEditor.ResetPasswordInvalidEmail");
            content += this.translationService.t("UserEditor.ResetPasswordEnsureValidEmail");
        }

        const dialog = this.$mdDialog
            .confirm()
            .title(this.translationService.t("UserEditor.SendResetPassword"))
            .htmlContent(content)
            .targetEvent($event)
            .ok(this.translationService.t("Common.Send"))
            .cancel(this.translationService.t("Common.Cancel"));

        this.$mdDialog.show(dialog).then(() => {
            this.sendForgotPasswordEmail();
        });
    }

    sendForgotPasswordEmail(): angular.IPromise<void> {
        if (this.resettingPassword) {
            return;
        }

        this.resettingPassword = true;

        return this.loginService.sendForgotPasswordEmail(this.dirtyUser.Email).then(() => {
            const passwordResultDialog = this.$mdDialog
                .alert()
                .title(this.translationService.t("UserEditor.ResetPasswordEmailSent"))
                .htmlContent(this.translationService.t("UserEditor.ResetPasswordLinkBeenSent", [this.dirtyUser.CurrentAccountUserMapping.Name, this.dirtyUser.Email]))
                .ok(this.translationService.t("Common.OK"));

            this.$mdDialog.show(passwordResultDialog);

        }).finally(() => {
            this.resettingPassword = false;
        });
    }

    loginAs(): void {
        if (!this.currentUserOnFoundationPlatform) {
            return;
        }

        this.usersService.getLoginToken(this.dirtyUser.Id).then((token: string) => {
            this.loginService.loginWithToken(token, true).then((response: ILoginResponse) => {
                this.$timeout(500).then(() => {
                    this.loginService.switchAccount(this.customerId, true);
                });
            });
        })
    }

    addGroup(group: IGroupWithIds) {
        const existingGroupIndex: number = this.dirtyUser.GroupIds.indexOf(group.Id);

        if (existingGroupIndex > -1) {
            return;
        }

        this.dirtyUser.GroupIds.push(group.Id);
    }

    removeGroup(group: IGroupWithIds) {
        const existingGroupIndex: number = this.dirtyUser.GroupIds.indexOf(group.Id);

        if (existingGroupIndex === -1) {
            return;
        }

        this.dirtyUser.GroupIds.splice(existingGroupIndex, 1);
    }

    removeGroupIncludingDuplicates(group: IGroupWithIds) {
        this.dirtyUser.GroupIds.filter(id => id == group.Id).forEach(id => { this.removeGroup(group); });
    }

    onReadFeatureChanged(accountFeature: AccountFeatures, enabled: boolean): void {
        if (!enabled) {
            this.accountFeatureService.disableUserMappingFeatureAccount(this.dirtyUser.CurrentAccountUserMapping, accountFeature, FeatureAccessType.Manage);
        }

        this.onFormChanged(true);
    }

    onManageFeatureChanged(accountFeature: AccountFeatures, enabled: boolean): void {
        if (enabled) {
            this.accountFeatureService.enableUserMappingFeatureAccess(this.dirtyUser.CurrentAccountUserMapping, accountFeature, FeatureAccessType.Read);
        }

        this.onFormChanged(true);
    }

    onFormChanged(digest: boolean = false): void {
        this.checkForUnsavedChanged();
        this.validateForm();
        this.updateResourceTotals();

        if (digest) {
            this.$scope.$digest();
        }
    }

    onCombinedManageFeatureChanged(feature: AccountFeatures): void {
        const hasManageFeature = (this.dirtyUser.CurrentAccountUserMapping.ManageFeatures & feature) === feature;

        if (hasManageFeature) {
            this.dirtyUser.CurrentAccountUserMapping.ReadFeatures = this.dirtyUser.CurrentAccountUserMapping.ReadFeatures | feature;
        } else {
            this.dirtyUser.CurrentAccountUserMapping.ReadFeatures = this.dirtyUser.CurrentAccountUserMapping.ReadFeatures & ~feature;
        }

        this.onFormChanged(true);
    }

    onSmsOutputChanged(output: IPhoneNumberOutput): void {
        if (!this.dirtyUser) {
            return;
        }

        if (!output.input) {
            this.isPhoneValid = true;
            this.dirtyUser.CurrentAccountUserMapping.Phone = null;
            this.onFormChanged();

            return;
        }

        this.isPhoneValid = output.isValid;
        this.dirtyUser.CurrentAccountUserMapping.Phone = output.internationalized;

        try {
            this.$scope.$applyAsync();
        }
        catch (e) {
            console.error(e);
        }

        this.onFormChanged();
    }


    onGroupClicked(group: IGroupWithIds) {
        if (group.selected) {
            this.addGroup(group);
        }
        else {
            this.removeGroupIncludingDuplicates(group);
        }

        this.refreshSelectedUserGroups();
        this.updateResourceTotals();
        this.onFormChanged();
        this.$scope.$apply();
    }

    onAllowAllVehiclesToggled(): void {
        this.dirtyUser.VehicleIds = (this.dirtyUser.CurrentAccountUserMapping.AllowAllVehicles) ? [] : this.cachedSelectedVehicleIds;

        this.onFormChanged(true);
    }

    onAllowAllDriversToggled(): void {
        this.dirtyUser.DriverIds = (this.dirtyUser.CurrentAccountUserMapping.AllowAllDrivers) ? [] : this.cachedSelectedDriverIds;

        this.onFormChanged(true);
    }

    onAllowAllGeofencesToggled(): void {
        this.dirtyUser.GeofenceIds = (this.dirtyUser.CurrentAccountUserMapping.AllowAllGeofences) ? [] : this.cachedSelectedGeofenceIds;

        this.onFormChanged(true);
    }

    onIncludedVehicleChanged(item: IResourceGroupListEntry<any>) {
        this.processChangedResource(item, "VehicleIds");

        this.cachedSelectedVehicleIds = this.dirtyUser.VehicleIds.slice();
    }

    onIncludedDriverChanged(item: IResourceGroupListEntry<any>) {
        this.processChangedResource(item, "DriverIds");

        this.cachedSelectedDriverIds = this.dirtyUser.DriverIds.slice();
    }

    onIncludedGeofenceChanged(item: IResourceGroupListEntry<any>) {
        this.processChangedResource(item, "GeofenceIds");

        this.cachedSelectedGeofenceIds = this.dirtyUser.GeofenceIds.slice();
    }

    createVehicleEntityCallback(vehicle: IVehicle, newInclusionState?: boolean): IResourceGroupListEntry<IVehicle> {
        let included: boolean;

        if (this.dirtyUser) {
            included = this.dirtyUser.CurrentAccountUserMapping?.AllowAllVehicles || this.dirtyUser.VehicleIds?.indexOf(vehicle.Id) >= 0;
        }

        if (typeof newInclusionState === "boolean") {
            included = newInclusionState;
        }

        return {
            id: vehicle.Id,
            data: vehicle,
            title: vehicle.ShortReg,
            included: included
        };
    }

    createDriverEntityCallback(driver: IDriver, newInclusionState?: boolean): IResourceGroupListEntry<IDriver> {
        let included: boolean;

        if (this.dirtyUser) {
            included = this.dirtyUser.CurrentAccountUserMapping?.AllowAllDrivers || this.dirtyUser.DriverIds?.indexOf(driver.Id) >= 0;
        }

        if (typeof newInclusionState === "boolean") {
            included = newInclusionState;
        }

        return {
            id: driver.Id,
            data: driver,
            title: driver.DisplayName,
            included: included
        };
    }

    createGeofenceEntityCallback(geofence: IGeofence, newInclusionState?: boolean): IResourceGroupListEntry<IGeofence> {
        let included: boolean;

        if (this.dirtyUser) {
            included = this.dirtyUser.CurrentAccountUserMapping?.AllowAllGeofences || this.dirtyUser.GeofenceIds?.indexOf(geofence.Id) >= 0;
        }

        if (typeof newInclusionState === "boolean") {
            included = newInclusionState;
        }

        return {
            id: geofence.Id,
            data: geofence,
            title: geofence.Description,
            included: included
        };
    }

    // ---

    private createUserSavePromise(): angular.IPromise<IAccountUserMapping> {
        if (this.dirtyUser.Id > 0) {
            return this.accountUserMappingService.postAccountUserMapping(this.dirtyUser.Email, this.dirtyUser.CurrentAccountUserMapping, this.dirtyUser.newUserLanguageCode);
        }

        return this.usersService.save(this.dirtyUser).then((userId) => {
            this.dirtyUser.Id = userId;

            return this.$timeout(1000).then(() => {
                return this.accountUserMappingService.postAccountUserMapping(this.dirtyUser.Email, this.dirtyUser.CurrentAccountUserMapping, this.dirtyUser.newUserLanguageCode);
            });
        })
    }

    private createResourceMappingsSavePromise(): angular.IPromise<any> {
        let mappingPromises: angular.IPromise<any>[] = [this.$q.resolve()];

        if (this.$rootScope.currentUser?.platform !== UserPlatform.Foundation) {
            mappingPromises = [
                this.userMappingsService.updateUserDriverMappings(this.dirtyUser.Id, this.dirtyUser.DriverIds),
                this.userMappingsService.updateUserVehicleMappings(this.dirtyUser.Id, this.dirtyUser.VehicleIds),
                this.userMappingsService.updateUserGeofenceMappings(this.dirtyUser.Id, this.dirtyUser.GeofenceIds),
                this.userMappingsService.updateUserGroupMappings(this.dirtyUser.Id, this.dirtyUser.GroupIds)
            ];
        }

        return this.$q.all(mappingPromises);
    }

    private createUserPreferencesSavePromise(accountUserMapping: IAccountUserMapping): angular.IPromise<IAccountUserMapping> {
        const preferences: IPreferencesContent = {} as any;

        const langPref: StringPreference = {
            Value: this.dirtyUser.newUserLanguageCode,
            Section: 4,
            savePreference: true
        }

        preferences.Language = langPref;

        return this.preferencesService.createNewUserPreferences(preferences, this.dirtyUser.Id, accountUserMapping.AccountId).then(() => {
            return accountUserMapping;
        });
    }

    private prepareUserModel(): void {
        if (!this.user?.CurrentAccountUserMapping) {
            return;
        }

        this.getData().then(() => {

            this.patchMissingUserProperties(this.user);
            this.dirtyUser = copy(this.user);
            this.cacheResourceIds();
            this.refreshSelectedUserGroups();

            return this.cacheUserMappings(this.dirtyUser);

        }).finally(() => {

            this.prepareAvailableAccountFeatures();
            this.updateResourceTotals();
            this.checkForUnsavedChanged();

            this.loading = false;
        });
    }

    private getData(): angular.IPromise<void> {
        this.loading = true;



        const localesPromise = this.translationService.getListOfLocales();
        const accountPromise = (this.customerId) ? this.foundationAccountService.getCustomerAccount(this.customerId) : this.accountService.getAccountDataFromCache();
        const driversPromise = (!this.currentUserOnFoundationPlatform) ? this.driverService.getDrivers() : this.$q.resolve<any>(null);
        const vehiclesPromise = (!this.currentUserOnFoundationPlatform) ? this.vehicleService.getVehicles({ includePeripheralMappings: true }) : this.$q.resolve<any>(null);
        const geofencesPromise = (!this.currentUserOnFoundationPlatform) ? this.geofenceService.getGeofences() : this.$q.resolve<any>(null);
        const groupsPromise = (!this.currentUserOnFoundationPlatform) ? this.groupService.getGroupsWithFullMappings() : this.$q.resolve<any>(null);
        const loginHistoryPromise = (!this.currentUserOnFoundationPlatform && this.user?.Id > -1)
            ? this.loginHistoryService.getLatestLoginHistoryForUser(this.user.Id)
            : this.$q.resolve<ILoginHistory>(null);

        return this.$q.all([localesPromise, accountPromise, driversPromise, vehiclesPromise, geofencesPromise, groupsPromise, loginHistoryPromise]).then(([locales, account, drivers, vehicles, geofences, groups, loginHistory]) => {

            this.locales = locales?.filter(locale => locale.Locale !== "qa");
            this.account = account;
            this.groups = groups;
            this.drivers = drivers;
            this.vehicles = vehicles;
            this.geofences = geofences;

            this.accountFeatureDescriptions = this.accountFeatureService.getFeatureDescriptionsForAccount(this.account)
            this.accountFeatureDescriptionsMap = this.accountFeatureService.getFeatureDescriptionsMapForAccount(this.account);
            this.specialUserPermissionsDescriptionsMap = this.accountFeatureService.getSpecialUserPermissionsDescriptionsMap();

            this.updateLastLoggedInDate(loginHistory);
        });
    }

    private cacheUserMappings(user: IUser): angular.IPromise<void> {
        const userHasCachedMappings = !!user.DriverIds || !!user.VehicleIds || !!user.GeofenceIds;

        if (userHasCachedMappings) {
            this.$q.resolve(null);
        }

        return this.$q.all([

            this.userMappingsService.getDriverMappingsByUserId(user.Id, false),
            this.userMappingsService.getVehicleMappingsByUserId(user.Id, false),
            this.userMappingsService.getGeofenceMappingsByUserId(user.Id, false)

        ]).then(([driverMappings, vehicleMappings, geofenceMappings]) => {

            user.DriverIds = driverMappings ? driverMappings.map(mapping => { return mapping.DriverId; }) : [];
            user.VehicleIds = vehicleMappings ? vehicleMappings.map(mapping => { return mapping.VehicleId; }) : [];
            user.GeofenceIds = geofenceMappings ? geofenceMappings.map(mapping => { return mapping.GeofenceId; }) : [];

            this.usersService.updateOrInsertUserCache(user);
        });
    }

    private patchMissingUserProperties(user: IUser): void {
        if (!user) {
            return;
        }

        if (typeof user.CurrentAccountUserMapping.Phone === "undefined") {
            user.CurrentAccountUserMapping.Phone = null;
        }

        if (typeof user.CurrentAccountUserMapping.ReadFeatures === "undefined" || user.CurrentAccountUserMapping.ReadFeatures === null) {
            user.CurrentAccountUserMapping.ReadFeatures = 0;
        }

        if (typeof user.CurrentAccountUserMapping.ManageFeatures === "undefined" || user.CurrentAccountUserMapping.ManageFeatures === null) {
            user.CurrentAccountUserMapping.ManageFeatures = 0;
        }
    }

    private checkForUnsavedChanged(): void {
        this.hasUnsavedChanges = !angular.equals(this.dirtyUser, this.user);
    }

    private validateForm(): void {
        this.isFormValid = this.checkUserValidity(this.dirtyUser) && this.isPhoneValid;
    }

    private checkUserValidity(user: IUser): boolean {
        if (!this.validateEmail(user.Email)) {
            return false;
        }

        return true;
    }

    private prepareAvailableAccountFeatures(): void {
        if (!this.accountFeatureDescriptionsMap) {
            return;
        }

        // Added allowance for TMS to support displaying Planning for accounts without TMS
        this.accountFeatureDescriptions = this.accountFeatureDescriptions.filter(accountFeature => accountFeature.enabled || accountFeature.feature === AccountFeatures.TMS);
    }

    private refreshSelectedUserGroups(): void {
        this.totalAssignedGroups = 0;

        this.recursivelySearchSelectedUserGroups();
    }

    private recursivelySearchSelectedUserGroups(groupIdsChecked: number[] = [], group?: IGroupWithIds) {
        const groupsToIterate: IGroup[] = group ? group.children : this.groups;

        if (!groupsToIterate) {
            return;
        }

        groupsToIterate.forEach((group: IGroupWithIds) => {
            if (groupIdsChecked.indexOf(group.Id) >= 0) {
                this.recursivelySearchSelectedUserGroups(groupIdsChecked, group);
                return;
            }

            groupIdsChecked.push(group.Id);

            group.selected = this.dirtyUser.GroupIds.indexOf(group.Id) >= 0;

            if (group.selected) {
                this.totalAssignedGroups++;
            }

            this.recursivelySearchSelectedUserGroups(groupIdsChecked, group);
        });
    }

    private processChangedResource(item: IResourceGroupListEntry<any>, arrayName: "VehicleIds" | "DriverIds" | "GeofenceIds") {
        const resourceArray: number[] = this.dirtyUser[arrayName];
        const currentIndex: number = resourceArray.indexOf(item.id);

        if (item.included) {
            resourceArray.push(item.id);
        }
        else if (currentIndex >= 0) {
            resourceArray.splice(currentIndex, 1);
        }

        this.updateResourceTotals();
        this.onFormChanged();
    }

    private updateResourceTotals(): void {
        if (!this.dirtyUser || this.currentUserOnFoundationPlatform || !this.vehicles || !this.drivers || !this.geofences) {
            return;
        }

        this.totalAssignedVehicles = (!this.dirtyUser.CurrentAccountUserMapping || !this.dirtyUser.CurrentAccountUserMapping.AllowAllVehicles)
            ? this.dirtyUser.VehicleIds.length
            : this.vehicles.length;

        this.totalAssignedDrivers = (!this.dirtyUser.CurrentAccountUserMapping || !this.dirtyUser.CurrentAccountUserMapping.AllowAllDrivers)
            ? this.dirtyUser.DriverIds.length
            : this.drivers.length;

        this.totalAssignedGeofences = (!this.dirtyUser.CurrentAccountUserMapping || !this.dirtyUser.CurrentAccountUserMapping.AllowAllGeofences)
            ? this.dirtyUser.GeofenceIds.length
            : this.geofences.length;
    }

    private updateLastLoggedInDate(loginHistory: ILoginHistory): void {
        this.lastLoggedInDate = (loginHistory) ? this.veriDate.formatDate(loginHistory.CreatedTime) : null;
    }

    private cacheResourceIds(): void {
        if (!this.dirtyUser) {
            return;
        }

        this.cachedSelectedVehicleIds = (this.dirtyUser.VehicleIds) ? this.dirtyUser.VehicleIds.slice() : [];
        this.cachedSelectedDriverIds = (this.dirtyUser.DriverIds) ? this.dirtyUser.DriverIds.slice() : [];
        this.cachedSelectedGeofenceIds = (this.dirtyUser.GeofenceIds) ? this.dirtyUser.GeofenceIds.slice() : [];
    }

    private validateEmail(email: string): boolean {
        if (!email) {
            return false;
        }

        const regEx = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return regEx.test(email.toLowerCase());
    }

}
export class UserEditorComponentOld implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            user: "<?",
            customerId: "<?",
            onSaved: "&",
            onCreated: "&",
            onDeactivated: "&",
            onReactivated: "&",
            onCancelled: "&",
        };

        this.controller = UserEditorComponentController;
        this.template = require("./userEditor.component.html");
    }
}
